<template>
  <div v-if="warrantyProgram" class="flex flex-col gap-3 md:flex-row">
    <div class="mr-4" style="max-width: 300px">
      <div class="break-long-word">
        <b>{{ warrantyProgram.name }}</b>
      </div>
      <div>Provider: {{ warrantyProgram.provider_name }}</div>
      <div>Labor Rate: {{ dollars(warrantyProgram.labor_rate_cents) }}</div>
      <div>Term (Years): {{ warrantyProgram.term_years }}</div>
      <div v-if="warrantyProgram.sku_number">
        SKU #: {{ warrantyProgram.sku_number }}
      </div>
      <div v-if="warrantyProgram.cost_cents">
        Program Cost: {{ dollars(warrantyProgram.cost_cents) }}
      </div>
    </div>
    <div class="d-flex space-around gap-4">
      <Datepicker
        v-model="value.purchase_date"
        name="*Purchase Date"
        hint-label="(MM/DD/YY)"
        placeholder="MM/DD/YY"
        :field-error-message="v$.value.purchase_date.$errors[0]?.$message"
      />
      <Datepicker
        v-model="value.effective_date"
        name="*Effective Date"
        hint-label="(MM/DD/YY)"
        placeholder="MM/DD/YY"
        :disabled="!value.purchase_date"
        :field-error-message="v$.value.effective_date.$errors[0]?.$message"
      />
      <Datepicker
        v-model="value.expiration_date"
        name="*Expiration Date"
        hint-label="(MM/DD/YY)"
        placeholder="MM/DD/YY"
        :disabled="!value.purchase_date"
        :field-error-message="v$.value.expiration_date.$errors[0]?.$message"
      />
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { dayMustBeAfterOrEqual } from "@/lib/vuelidate_helpers";

import Datepicker from "@/components/Datepicker.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

export default {
  components: {
    Datepicker
  },
  props: {
    warrantyProgram: {
      type: Object,
      default: null
    },
    modelValue: {
      type: Object,
      default: () => ({})
    },
    editing: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue"],
  setup() {
    return { v$: useVuelidate() };
  },
  computed: {
    value: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
      }
    }
  },
  watch: {
    "value.purchase_date"(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updatedWarrantyEffectiveDate();
      }
    },
    "value.effective_date"(newValue, oldValue) {
      if (newValue !== oldValue && newValue) {
        this.updateWarrantyExpirationDate();
      }
    }
  },
  validations() {
    return {
      value: {
        purchase_date: { required, $autoDirty: true },
        effective_date: {
          required,
          afterPurchaseDate: dayMustBeAfterOrEqual(
            "purchase_date",
            "Cannot be before purchase date"
          ),
          $autoDirty: true
        },
        expiration_date: {
          required,
          afterPurchaseDate: dayMustBeAfterOrEqual(
            "purchase_date",
            "Cannot be before purchase date"
          ),
          afterEffectiveDate: dayMustBeAfterOrEqual(
            "effective_date",
            "Cannot be before effective date"
          ),
          $autoDirty: true
        }
      }
    };
  },
  methods: {
    updatedWarrantyEffectiveDate() {
      // Only auto-update when adding a new warranty
      // or editing and there is no data
      const { purchase_date, effective_date } = this.value;
      if (
        !purchase_date ||
        !this.warrantyProgram ||
        (this.editing && !!effective_date)
      )
        return;

      const newValue = {
        ...this.value,
        effective_date: moment(purchase_date)
          .add(this.warrantyProgram.effectiveAfterDays, "days")
          .toISOString()
      };
      this.$emit("update:modelValue", newValue);
    },
    updateWarrantyExpirationDate() {
      // Only auto-update when adding a new warranty
      // or editing and there is no data
      const { effective_date, expiration_date } = this.value;
      if (
        !effective_date ||
        !this.warrantyProgram ||
        (this.editing && !!expiration_date)
      )
        return;

      const newValue = {
        ...this.value,
        expiration_date: moment(effective_date)
          .add(this.warrantyProgram.term_years, "year")
          .toISOString()
      };
      this.$emit("update:modelValue", newValue);
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(.date-picker-label) {
  display: block;
}
</style>
